/**
 * Raise Alert feature — constants for the critical-alert UI.
 */

/**
 * Client-side kill switch. NOT the main control any more.
 * true  -> Submit scans the report and opens the modal when something fires
 * false -> Submit behaves exactly as it did before this feature existed,
 *          whatever the database says
 */
export const ENABLE_CRITICAL_ALERT_SCAN = true;

/**
 * Scan every report tab, or only the one on screen?
 *
 * true  -> the visible tab's live editor content PLUS every other tab's content
 *          from patientReports. patientReports is what the submit payload is
 *          built from, so this scans exactly what gets saved.
 * false -> only the visible tab. A critical finding typed into another tab is
 *          then missed silently, with no error and no log.
 *
 * Open question #18 ("which text is scanned") is still unanswered by product, so
 * this is a flag rather than a hardcoded choice.
 */
export const SCAN_ALL_REPORT_TABS = true;

/** Colour + label per priority. Same palette in light and dark. */
/**
 * How many evidence sentences are persisted per finding.
 *
 * study_critical_alert NEVER deletes -- a resubmit retires the previous set and inserts
 * a new one -- so anything stored per finding is stored forever. A cap keeps a pathological
 * report (one that argues the same finding twenty ways) from bloating the table. Ten is far
 * above anything seen in practice; the busiest real report so far produced five.
 */
export const MAX_SAVED_OCCURRENCES = 10;

/** Matches study_critical_alert.matched_sentence VARCHAR(500). */
export const MAX_SAVED_SENTENCE = 500;

export const PRIORITY_META = {
  CRITICAL: {
    label: "Critical",
    color: "#E04A4E",
    softBg: "rgba(224, 74, 78, 0.14)",
  },
  URGENT: {
    label: "Urgent",
    color: "#D98700",
    softBg: "rgba(217, 135, 0, 0.14)",
  },
  HIGH_CONCERN: {
    label: "High concern",
    color: "#2F86D9",
    softBg: "rgba(47, 134, 217, 0.14)",
  },
};

export const getPriorityMeta = (priority) =>
  PRIORITY_META[priority] || PRIORITY_META.HIGH_CONCERN;

/**
 * Solid badge colours, for surfaces that show ONE badge standing for the whole
 * study rather than a list of findings -- the studylist ALERT RAISED badge today.
 *
 * Deliberately the same values the report email's finding badges already use, so
 * a study that reads red in the studylist reads red in the clinician's inbox.
 * Change them here and change the email template with them.
 *
 * These are solid fills rather than PRIORITY_META's 14% softBg, which is built for
 * the modal's light finding rows: a 14% tint over the studylist's dark row does not
 * read as red or amber at all, and the whole point of the badge is the colour.
 */
export const PRIORITY_BADGE = {
  // OLD: solid fills that did not match the modal
  //   CRITICAL:     { bg: "#C62828", text: "#FFFFFF" },
  //   URGENT:       { bg: "#EF6C00", text: "#FFFFFF" },
  //   HIGH_CONCERN: { bg: "#1565C0", text: "#FFFFFF" },
  // NEW 10-Sep-2026: the same three hues PRIORITY_META uses for the modal's severity
  // chips, so a study reads the same colour in the studylist as in the Alert Raised
  // dialog. Kept as a solid fill with white text -- PRIORITY_META's 14% softBg is built
  // for the modal's light finding rows and disappears against the studylist's dark row.
  CRITICAL: { bg: PRIORITY_META.CRITICAL.color, text: "#FFFFFF" },
  URGENT: { bg: PRIORITY_META.URGENT.color, text: "#FFFFFF" },
  HIGH_CONCERN: { bg: PRIORITY_META.HIGH_CONCERN.color, text: "#FFFFFF" },
};

/**
 * For a study whose alert carries no resolvable priority: an alert raised before this
 * feature existed, so it has no study_critical_alert rows. Unchanged from what the
 * badge looked like before it was coloured, so nothing regresses.
 */
export const PRIORITY_BADGE_FALLBACK = {
  bg: "rgb(245 173 41 / 69%)",
  text: "rgb(55 38 6)",
};

export const getPriorityBadge = (priority) =>
  PRIORITY_BADGE[priority] || PRIORITY_BADGE_FALLBACK;